import { useState } from 'react';
import { supabase } from '@/lib/customSupabaseClient';
import { useAuth } from '@/contexts/SupabaseAuthContext';
import { useToast } from '@/components/ui/use-toast';
import { downloadBackupFile, parseBackupFile } from '@/services/BackupService';

export const useBackupRestore = (forceReloadData) => {
    const { user, activeAccountId } = useAuth();
    const { toast } = useToast();
    const [isExporting, setIsExporting] = useState(false);
    const [isImporting, setIsImporting] = useState(false);

    const handleExportBackup = async () => {
        if (!activeAccountId) return;
        setIsExporting(true);

        try {
            // Buscar todos os dados
            const [
                { data: operators },
                { data: flags },
                { data: paymentMethods },
                { data: operatorFlags },
                { data: aliases },
                { data: sales },
                { data: receipts },
                { data: platformAdjustments }
            ] = await Promise.all([
                supabase.from('operators').select('*').eq('user_id', activeAccountId),
                supabase.from('flags').select('*').eq('user_id', activeAccountId),
                supabase.from('payment_methods').select('*').eq('user_id', activeAccountId),
                supabase.from('operator_flags').select('*').eq('user_id', activeAccountId),
                supabase.from('aliases').select('*').eq('user_id', activeAccountId),
                supabase.from('sales').select('*').eq('user_id', activeAccountId),
                supabase.from('receipts').select('*').eq('user_id', activeAccountId),
                supabase.from('platform_adjustments').select('*').eq('user_id', activeAccountId)
            ]);

            const backupData = {
                metadata: {
                    version: "1.1",
                    type: "conciliador_backup",
                    export_date: new Date().toISOString(),
                    user_id: activeAccountId
                },
                lookups: {
                    operators: operators || [],
                    flags: flags || [],
                    payment_methods: paymentMethods || [],
                    operator_flags: operatorFlags || [],
                    aliases: aliases || []
                },
                transactions: {
                    sales: sales || [],
                    receipts: receipts || [],
                    platform_adjustments: platformAdjustments || []
                }
            };

            const dataString = new Date().toISOString().split('T')[0];
            // Usa extensão .bkp conforme escolha do usuário
            downloadBackupFile(backupData, `backup_conciliador_${dataString}.bkp`);
            toast({ title: "Backup gerado com sucesso!" });

        } catch (error) {
            console.error("Erro ao gerar backup:", error);
            toast({ title: "Erro ao gerar backup", description: error.message, variant: "destructive" });
        } finally {
            setIsExporting(false);
        }
    };

    const handleImportBackup = async (file) => {
        if (!activeAccountId || !file) return;
        setIsImporting(true);

        try {
            const data = await parseBackupFile(file);

            // === 1. LIMPAR DADOS ANTIGOS (Ordem importa para FK constraints) ===
            // Dependentes primeiro
            await Promise.all([
                supabase.from('operator_flags').delete().eq('user_id', activeAccountId),
                supabase.from('aliases').delete().eq('user_id', activeAccountId),
                supabase.from('receipts').delete().eq('user_id', activeAccountId),
                supabase.from('sales').delete().eq('user_id', activeAccountId),
                supabase.from('platform_adjustments').delete().eq('user_id', activeAccountId)
            ]);
            // Principais depois
            await Promise.all([
                supabase.from('operators').delete().eq('user_id', activeAccountId),
                supabase.from('flags').delete().eq('user_id', activeAccountId),
                supabase.from('payment_methods').delete().eq('user_id', activeAccountId)
            ]);

            // === 2. INSERIR LOOKUPS (omitindo id, banco auto-gera bigint) ===
            // Mapeamento: id_antigo -> id_novo (para chaves estrangeiras)
            const operatorIdMap = {};
            const flagIdMap = {};

            // -- Operators --
            const backupOperators = data.lookups.operators || [];
            if (backupOperators.length > 0) {
                const toInsert = backupOperators.map(({ id, ...rest }) => ({ ...rest, user_id: activeAccountId }));
                const { data: inserted, error } = await supabase.from('operators').insert(toInsert).select();
                if (error) throw error;
                // Mapear pelo nome: old_id -> new_id
                backupOperators.forEach(old => {
                    const match = inserted.find(n => n.name === old.name);
                    if (match) operatorIdMap[old.id] = match.id;
                });
            }

            // -- Flags --
            const backupFlags = data.lookups.flags || [];
            if (backupFlags.length > 0) {
                const toInsert = backupFlags.map(({ id, ...rest }) => ({ ...rest, user_id: activeAccountId }));
                const { data: inserted, error } = await supabase.from('flags').insert(toInsert).select();
                if (error) throw error;
                backupFlags.forEach(old => {
                    const match = inserted.find(n => n.name === old.name);
                    if (match) flagIdMap[old.id] = match.id;
                });
            }

            // -- Payment Methods --
            const backupPM = data.lookups.payment_methods || [];
            if (backupPM.length > 0) {
                const toInsert = backupPM.map(({ id, ...rest }) => ({ ...rest, user_id: activeAccountId }));
                const { error } = await supabase.from('payment_methods').insert(toInsert);
                if (error) throw error;
            }

            // -- Operator Flags (usa os novos IDs mapeados) --
            const backupOF = data.lookups.operator_flags || [];
            if (backupOF.length > 0) {
                const toInsert = backupOF.map(({ id, ...rest }) => ({
                    ...rest,
                    user_id: activeAccountId,
                    operator_id: operatorIdMap[rest.operator_id] || rest.operator_id,
                    flag_id: flagIdMap[rest.flag_id] || rest.flag_id
                }));
                const { error } = await supabase.from('operator_flags').insert(toInsert);
                if (error) throw error;
            }

            // -- Aliases --
            const backupAliases = data.lookups.aliases || [];
            if (backupAliases.length > 0) {
                const toInsert = backupAliases.map(({ id, ...rest }) => ({ ...rest, user_id: activeAccountId }));
                const { error } = await supabase.from('aliases').insert(toInsert);
                if (error) throw error;
            }

            // === 3. INSERIR TRANSAÇÕES EM LOTES ===
            const insertInBatches = async (table, items) => {
                for (let i = 0; i < items.length; i += 1000) {
                    const chunk = items.slice(i, i + 1000);
                    const { error } = await supabase.from(table).insert(chunk);
                    if (error) throw error;
                }
            };

            // -- Sales (remove id, deixa banco gerar) --
            const backupSales = data.transactions.sales || [];
            if (backupSales.length > 0) {
                const toInsert = backupSales.map(({ id, ...rest }) => ({ ...rest, user_id: activeAccountId }));
                await insertInBatches('sales', toInsert);
            }

            // -- Receipts (remove id, mapeia operator_id) --
            const backupReceipts = data.transactions.receipts || [];
            if (backupReceipts.length > 0) {
                const toInsert = backupReceipts.map(({ id, ...rest }) => ({
                    ...rest,
                    user_id: activeAccountId,
                    operator_id: rest.operator_id ? (operatorIdMap[rest.operator_id] || rest.operator_id) : null
                }));
                await insertInBatches('receipts', toInsert);
            }

            // -- Platform Adjustments (Despesas iFood) --
            const backupAdjustments = data.transactions.platform_adjustments || [];
            if (backupAdjustments.length > 0) {
                const toInsert = backupAdjustments.map(({ id, ...rest }) => ({
                    ...rest,
                    user_id: activeAccountId,
                    operator_id: rest.operator_id ? (operatorIdMap[rest.operator_id] || rest.operator_id) : null
                }));
                await insertInBatches('platform_adjustments', toInsert);
            }

            // === 4. RECONCILIAÇÃO AUTOMÁTICA ===
            await supabase.rpc('rpc_reconciliar_vendas', { p_user_id: activeAccountId, p_mode: 'sum' });

            toast({ title: "Sucesso!", description: "Backup restaurado perfeitamente." });
            
            if (forceReloadData) forceReloadData();

        } catch (error) {
            console.error("Erro ao importar backup:", error);
            toast({ title: "Erro de Restauração", description: error.message, variant: "destructive" });
        } finally {
            setIsImporting(false);
        }
    };

    return {
        isExporting,
        isImporting,
        handleExportBackup,
        handleImportBackup
    };
};
